/**
 * STORY-304: the actions under an incident card answer — download it as
 * Markdown, print it (or save it as PDF from the print dialog), and send it
 * to the user's own linked Telegram chat. Shown only on a message whose
 * metadata carries `incidentCard` (the backend's marker). "Send to Telegram"
 * appears only when GET /api/settings/telegram says the account is linked;
 * the server re-redacts the text and sends nothing if it cannot.
 */
import React, { useEffect, useState } from 'react';
import { FileDown, Printer, Send } from 'lucide-react';
import { apiClient } from '@/utils/apiClient';
import type { IncidentCardMeta } from '@/utils/apiClient';
import { cardGeneratedAt, downloadIncidentCard, printIncidentCard } from '@/utils/incidentCard';
import { useTranslation } from '@/i18n';
import { toast } from '@/stores/toastStore';

// The link status is asked once a minute at most, not once per card.
const LINK_STATUS_TTL_MS = 60_000;
let linkStatus: { at: number; linked: Promise<boolean> } | null = null;

export function telegramLinked(now: number = Date.now()): Promise<boolean> {
  if (!linkStatus || now - linkStatus.at > LINK_STATUS_TTL_MS) {
    linkStatus = {
      at: now,
      linked: apiClient
        .getTelegramStatus()
        .then((s) => Boolean(s?.is_linked && s?.is_active))
        .catch(() => false),
    };
  }
  return linkStatus.linked;
}

/** Tests: forget the cached link status. */
export function resetTelegramLinkStatus(): void {
  linkStatus = null;
}

// The Schedule button's look, always visible (not hover-only) so keyboard
// and touch users reach it.
const BUTTON =
  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 ' +
  'bg-gray-100 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 ' +
  'dark:hover:text-blue-400 rounded-lg border border-transparent hover:border-blue-200 ' +
  'dark:hover:border-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed';

interface IncidentCardActionsProps {
  content: string;
  card: IncidentCardMeta;
  /** The element the print shows: the card's rendered text. */
  printTarget: React.RefObject<HTMLElement | null>;
}

export const IncidentCardActions: React.FC<IncidentCardActionsProps> = ({ content, card, printTarget }) => {
  const { t } = useTranslation();
  const [linked, setLinked] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    telegramLinked().then((ok) => {
      if (alive) setLinked(ok);
    });
    return () => {
      alive = false;
    };
  }, []);

  const handleDownload = () => {
    const name = downloadIncidentCard(content, cardGeneratedAt(card.generated_at));
    toast.success(t.incidentCard.downloaded.replace('{name}', name));
  };

  const handlePrint = () => {
    if (printTarget.current) printIncidentCard(printTarget.current);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await apiClient.sendCardToTelegram(content, card.problem_id);
      toast.success(t.incidentCard.sent);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(t.incidentCard.sendFailed.replace('{message}', message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="group"
      aria-label={t.incidentCard.actions}
      data-incident-card-actions
      className="mt-2 flex flex-wrap items-center gap-2"
    >
      <button type="button" className={BUTTON} onClick={handleDownload} aria-label={t.incidentCard.downloadLabel}>
        <FileDown className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{t.incidentCard.download}</span>
      </button>
      <button type="button" className={BUTTON} onClick={handlePrint} aria-label={t.incidentCard.printLabel}>
        <Printer className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{t.incidentCard.print}</span>
      </button>
      {linked && (
        <button
          type="button"
          className={BUTTON}
          onClick={handleSend}
          disabled={sending}
          aria-label={t.incidentCard.sendTelegramLabel}
          aria-busy={sending}
        >
          <Send className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{sending ? t.incidentCard.sending : t.incidentCard.sendTelegram}</span>
        </button>
      )}
    </div>
  );
};
